export interface News {
  id: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  image?: string;
  imageUrl?: string;
  type: string;
  category?: {
    id: number;
    name: string;
    slug: string;
  };
  keywords: string[];
  metaDescription?: string;
  focusKeyword?: string;
  seoScore: number;
  readabilityScore: number;
  publishedDate: string;
  views: number;
  like: number;
  author: string;
  isDraft: boolean;
  isDeleted: boolean;
  status: 'draft' | 'published' | 'scheduled';
  scheduledPublishDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NewsListParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  status?: string;
  includeDrafts?: boolean;
  sortBy?: string;
  order?: 'asc' | 'desc';
  viewScope?: 'own' | 'all';
}

export interface NewsListResponse {
  success: boolean;
  data: News[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
  message: string;
}

export interface NewsResponse {
  success: boolean;
  data: News;
  message: string;
}

export interface BulkActionResponse {
  success: boolean;
  data: {
    count: number;
  };
  message: string;
}

export interface SEOAnalysis {
  title: {
    score: number;
    issues: string[];
    suggestions: string[];
  };
  content: {
    score: number;
    issues: string[];
    suggestions: string[];
  };
  metaDescription: {
    score: number;
    issues: string[];
    suggestions: string[];
  };
  slug: {
    score: number;
    issues: string[];
    suggestions: string[];
  };
  readability: number;
  keywordDensity: number;
  overallScore: number;
}

export interface KeywordData {
  keyword: string;
  searchVolume: number;
  competition: string;
  relatedKeywords: string[];
  searchResults: {
    totalResults: number;
    topResults: Array<{
      title: string;
      url: string;
      description: string;
    }>;
  };
  suggestions: string[];
}

export interface BulkActionData {
  action: 'delete' | 'restore' | 'permanentDelete' | 'toggleStatus' | 'publish' | 'unpublish';
  newsIds: number[];
  status?: string;
}

export interface NewsCountResponse {
  success: boolean;
  data: {
    published: number;
    draft: number;
    deleted: number;
    total: number;
  };
  message: string;
}

interface GetNewsOptions {
  forcePublic?: boolean;
}

import { API_HEAD } from '@/lib/apiConfig';

class NewsService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_HEAD;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Đảm bảo luôn lấy token mới nhất từ localStorage
    let token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;
    
    // Nếu không có token, thử refresh hoặc throw error
    if (!token) {
      // Nếu đang ở client-side và không có token, có thể token đã bị xóa
      if (typeof window !== 'undefined') {
        console.error('❌ No token found in localStorage for request to:', endpoint);
        throw new Error('Không có token xác thực. Vui lòng đăng nhập lại.');
      }
    }
    
    // Log token status trong development (không log token thực tế vì bảo mật)
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
      console.log(`🔑 Making authenticated request to: ${endpoint}`, { hasToken: !!token });
    }
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    let response = await fetch(`${this.baseUrl}${endpoint}`, config);
    
    // Nếu 401 (Unauthorized), thử refresh token và retry
    if (response.status === 401 && token && typeof window !== 'undefined') {
      console.warn('⚠️ Received 401, attempting to refresh token...');
      try {
        // baseUrl đã có /api, chỉ cần thêm /admin/refresh-token
        const refreshResponse = await fetch(`${this.baseUrl}/admin/refresh-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          if (refreshData.success && refreshData.token) {
            localStorage.setItem('adminToken', refreshData.token);
            console.log('✅ Token refreshed successfully');
            // Retry request với token mới
            const newToken = refreshData.token;
            const retryConfig: RequestInit = {
              ...config,
              headers: {
                ...config.headers,
                'Authorization': `Bearer ${newToken}`
              }
            };
            response = await fetch(`${this.baseUrl}${endpoint}`, retryConfig);
          } else {
            console.error('❌ Token refresh failed: Invalid response');
            localStorage.removeItem('adminToken');
            throw new Error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
          }
        } else {
          console.error('❌ Token refresh failed:', refreshResponse.status);
          localStorage.removeItem('adminToken');
          throw new Error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        }
      } catch (refreshError) {
        // Nếu refresh thất bại, xóa token và throw error
        console.error('❌ Token refresh error:', refreshError);
        localStorage.removeItem('adminToken');
        throw new Error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
      }
    }
    
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Xử lý lỗi xác thực đặc biệt
      if (response.status === 401) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('adminToken');
        }
        throw new Error(errorData.message || 'Không có token xác thực. Vui lòng đăng nhập lại.');
      }
      
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  private async publicRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Public method - không yêu cầu đăng nhập (dùng cho trang public)
  async getNews(
    params: NewsListParams = {},
    options: GetNewsOptions = {}
  ): Promise<NewsListResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const url = `/news?${queryParams.toString()}`;

    if (options.forcePublic) {
      return this.publicRequest<NewsListResponse>(url);
    }

    return this.request<NewsListResponse>(url);
  }

  // Lấy tổng số bài viết
  async getNewsCount(type?: string): Promise<NewsCountResponse> {
    const queryParams = new URLSearchParams();
    if (type) {
      queryParams.append('type', type);
    }
    
    const url = queryParams.toString() 
      ? `/news/count?${queryParams.toString()}` 
      : '/news/count';
    
    return this.publicRequest<NewsCountResponse>(url);
  }

  // Public method - không yêu cầu đăng nhập (dùng cho trang public)
  async getNewsById(
    id: string,
    skipViewCount: boolean = false,
    options: { viewScope?: 'own' | 'all' } = {}
  ): Promise<NewsResponse> {
    const queryParams = new URLSearchParams();
    
    if (skipViewCount) {
      queryParams.append('skipViewCount', 'true');
    }
    
    if (options.viewScope) {
      queryParams.append('viewScope', options.viewScope);
    }
    
    const queryString = queryParams.toString();
    const url = queryString ? `/news/${id}?${queryString}` : `/news/${id}`;
    
    // Luôn dùng publicRequest cho trang public
    return this.publicRequest<NewsResponse>(url);
  }

  // Tạo tin tức mới
  async createNews(newsData: Partial<News>): Promise<NewsResponse> {
    return this.request<NewsResponse>('/news', {
      method: 'POST',
      body: JSON.stringify(newsData),
    });
  }

  // Cập nhật tin tức
  async updateNews(id: string, newsData: Partial<News>): Promise<NewsResponse> {
    return this.request<NewsResponse>(`/news/${id}`, {
      method: 'PUT',
      body: JSON.stringify(newsData),
    });
  }

  // Xóa tin tức (đánh dấu xóa)
  async deleteNews(id: string): Promise<NewsResponse> {
    return this.request<NewsResponse>(`/news/${id}`, {
      method: 'DELETE',
    });
  }

  // Xóa nhiều tin tức
  async deleteManyNews(ids: string[]): Promise<NewsResponse> {
    return this.request<NewsResponse>('/news/delete-many', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  // Khôi phục tin tức
  async restoreNews(id: string): Promise<NewsResponse> {
    return this.request<NewsResponse>(`/news/${id}/restore`, {
      method: 'PATCH',
    });
  }

  // Xóa vĩnh viễn tin tức
  async permanentDeleteNews(id: string): Promise<NewsResponse> {
    return this.request<NewsResponse>(`/news/${id}/permanent`, {
      method: 'DELETE',
    });
  }

  // Lưu bản nháp
  async saveDraft(newsData: Partial<News>): Promise<NewsResponse> {
    return this.request<NewsResponse>('/news/draft', {
      method: 'POST',
      body: JSON.stringify(newsData),
    });
  }

  // Xuất bản bản nháp
  async publishDraft(id: string, scheduledPublishDate?: string): Promise<NewsResponse> {
    const body: { scheduledPublishDate?: string } = {};
    // Chỉ gửi scheduledPublishDate nếu có giá trị (để hẹn giờ)
    // Nếu không có, xuất bản ngay lập tức
    if (scheduledPublishDate) {
      body.scheduledPublishDate = scheduledPublishDate;
    }
    return this.request<NewsResponse>(`/news/${id}/publish`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  // Lấy danh sách bản nháp
  async getDrafts(params: NewsListParams = {}): Promise<NewsListResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    return this.request<NewsListResponse>(`/news/drafts?${queryParams.toString()}`);
  }

  // Lấy tin tức trong thùng rác
  async getTrashNews(params: NewsListParams = {}): Promise<NewsListResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    return this.request<NewsListResponse>(`/news/trash?${queryParams.toString()}`);
  }

  // Upload ảnh tin tức
  async uploadNewsImage(file: File): Promise<{ success: boolean; imagePath: string; message: string }> {
    const formData = new FormData();
    formData.append('image', file);

    const token = localStorage.getItem('adminToken');
    
    const response = await fetch(`${this.baseUrl}/news/upload-image`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Phân tích SEO
  async analyzeSEO(data: {
    title: string;
    content: string;
    metaDescription?: string;
    focusKeyword?: string;
    slug?: string;
  }): Promise<{ success: boolean; data: SEOAnalysis; message: string }> {
    return this.request('/news/seo/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Tìm kiếm từ khóa
  async searchKeywords(keyword: string, limit: number = 10): Promise<{ success: boolean; data: KeywordData; message: string }> {
    return this.request(`/news/seo/keywords?keyword=${encodeURIComponent(keyword)}&limit=${limit}`);
  }

  // Lấy gợi ý từ khóa
  async getKeywordSuggestions(keyword: string, category?: string): Promise<{ success: boolean; data: string[]; message: string }> {
    const params = new URLSearchParams({ keyword });
    if (category) params.append('category', category);
    
    return this.request(`/news/seo/suggestions?${params.toString()}`);
  }

  // Thao tác hàng loạt
  async bulkAction(data: BulkActionData): Promise<BulkActionResponse> {
    return this.request<BulkActionResponse>('/news/bulk-action', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Lấy thống kê scheduler
  async getSchedulerStats(): Promise<{ success: boolean; data: any; message: string }> {
    return this.request('/news/scheduler/stats');
  }

  // Lấy danh sách tin tức chờ xuất bản
  async getPendingNews(): Promise<{ success: boolean; data: News[]; message: string }> {
    return this.request('/news/scheduler/pending');
  }

  // Hủy lịch xuất bản
  async cancelScheduledNews(id: string): Promise<NewsResponse> {
    return this.request(`/news/${id}/cancel-schedule`, {
      method: 'PATCH',
    });
  }

  // Cập nhật lịch xuất bản
  async updateScheduledNews(id: string, scheduledPublishDate: string): Promise<NewsResponse> {
    return this.request(`/news/${id}/update-schedule`, {
      method: 'PATCH',
      body: JSON.stringify({ scheduledPublishDate }),
    });
  }

  // Submit URL lên Google Search Console
  async submitToGoogle(url: string): Promise<{ success: boolean; data: any; message: string }> {
    return this.request('/news/seo/submit-google', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  // Kiểm tra trạng thái index
  async checkIndexStatus(url: string): Promise<{ success: boolean; data: any; message: string }> {
    return this.request(`/news/seo/check-index?url=${encodeURIComponent(url)}`);
  }

  // Tạo sitemap
  async generateSitemap(): Promise<string> {
    const response = await fetch(`${this.baseUrl}/news/seo/sitemap`);
    return response.text();
  }

  // Tạo robots.txt
  async generateRobots(): Promise<string> {
    const response = await fetch(`${this.baseUrl}/news/seo/robots`);
    return response.text();
  }
}

export default new NewsService();
